import useAction from '~/prix/react/hooks/action'
import userSelfDefinition from '../../self/userSelf.definition'
import userFetchDefinition from '../userFetch.definition'
import Handle from '~/prix/react/components/handle'
import Header from '~/components/card/header'
import styled from 'styled-components'
import { Input } from '~/packages/general/detailedInformation/styles'
import Label from '~/components/label'
import { userRoleEnum } from '../../userObject'
import { useNavigate, useParams } from 'react-router'
import EditIcon from '~/components/icons/ui/24px_edit-72.svg'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import { useEffect, useMemo, useState } from 'react'
import useAPI from '~/prix/react/hooks/api'
import { canListUsers } from '../../userManagementPermissions'
import { format } from 'date-fns'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 870px) {
    padding: 30px 0;
  }
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.padding(1)};
  span {
    margin: 0;
  }
`

const Container = styled.div`
  padding: ${({ theme }) => theme.padding(2)};
  background-color: #fff;
  margin-bottom: 1rem;
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius};
  border-bottom-right-radius: ${({ theme }) => theme.borderRadius};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.padding(2)};
`

const Message = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.darkGrey};
  font-size: 14px;
`

const TransparentButton = styled.button`
  display: flex;
  flex: 1;
  background-color: transparent;
  border: none;
  text-align: center;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  cursor: pointer;

  &[disabled] {
    opacity: 0.6;
    background-color: transparent;
    border: none;
  }
`

type UserFetchData = {
  id: string
  name: string
  email: string
  role: string
  firebaseData?: {
    displayName?: string | null
    photoURL?: string | null
    phoneNumber?: string | null
    lastSignInTime?: string | null
    creationTime?: string | null
    disabled: boolean
    emailVerified: boolean
  }
}

function getUserFetchErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : String(error)
  const normalizedMessage = message.toLowerCase()

  if (normalizedMessage.includes('não foi encontrado no firebase')) {
    return 'Usuário encontrado no banco, mas não encontrado no Firebase.'
  }

  return `Erro ao recuperar usuário. ${message}`
}

function formatFirebaseDate(value?: string | null) {
  if (!value) {
    return 'Não informado'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  return format(date, 'dd/LL/yyyy HH:mm')
}

const UserFetchScreen = () => {
  const navigate = useNavigate()
  const { id } = useParams() as { id?: string }
  const isAdministrativeDetail = Boolean(id)
  const [userData, setUserData] = useState<UserFetchData | null>(null)
  const [fetchErrorMessage, setFetchErrorMessage] = useState<string | null>(null)
  const { context } = useAPI()
  const actorRole = context.user?.activeClient?.roles?.[0]
  const canEditUsers = canListUsers(actorRole)
  const userProfileData = useAction(userSelfDefinition, {}, [], {
    autoLoad: !isAdministrativeDetail,
    staleWhileRevalidate: false,
  })
  const { callAction: callUserFetch, isLoading: isLoadingFetch } = useActionFunction(
    userFetchDefinition,
    {
      initialIsLoading: false,
    },
  )
  const { result, isLoading } = userProfileData
  const profileUserData = result?.userProfile

  useEffect(() => {
    if (!id) {
      return
    }

    setUserData(null)
    setFetchErrorMessage(null)
    const { resultPromise } = callUserFetch({ id })

    resultPromise
      .then(result => {
        const fetchedUser = result?.data?.[0] ?? null
        setUserData(fetchedUser)

        if (!fetchedUser) {
          setFetchErrorMessage('Usuário não encontrado.')
        }
      })
      .catch(error => {
        setFetchErrorMessage(getUserFetchErrorMessage(error))
      })
  }, [id, callUserFetch])

  const displayedUser = useMemo(() => {
    if (isAdministrativeDetail) {
      return userData
    }

    return profileUserData
      ? {
          id: profileUserData.id,
          name: profileUserData.displayName ?? '',
          email: profileUserData.email,
          role: profileUserData.activeClient?.roles?.[0],
        }
      : null
  }, [isAdministrativeDetail, profileUserData, userData])

  const targetUserId = displayedUser?.id ?? id
  const roleLabel =
    userRoleEnum.find(item => item.value === displayedUser?.role)?.label ?? displayedUser?.role ?? ''

  return (
    <Handle isLoading={isAdministrativeDetail ? isLoadingFetch : isLoading} style={{ height: '100%' }}>
      <Wrapper>
        <Header
          title={isAdministrativeDetail ? 'Detalhes do usuário' : 'Meu perfil'}
          extraButtons={
            canEditUsers && targetUserId ? (
              <TransparentButton
                onClick={() => navigate(`/app/users/update/${targetUserId}`)}
                title='Editar'
              >
                <EditIcon width={14} height={14} fill='#fff' />
              </TransparentButton>
            ) : undefined
          }
        />
        <Container>
          {fetchErrorMessage ? <Message>{fetchErrorMessage}</Message> : null}
          {!fetchErrorMessage ? (
            <>
              <Field>
                <Label label='Nome' size='sm' />
                <Input value={displayedUser?.name ?? ''} readOnly />
              </Field>
              <Field>
                <Label label='E-mail' size='sm' />
                <Input value={displayedUser?.email ?? ''} readOnly />
              </Field>
              <Field>
                <Label label='Cargo' size='sm' />
                <Input value={roleLabel} readOnly />
              </Field>
              {isAdministrativeDetail && userData?.firebaseData ? (
                <>
                  <Field>
                    <Label label='Conta Firebase' size='sm' />
                    <Input value={userData.firebaseData.disabled ? 'Desativada' : 'Ativa'} readOnly />
                  </Field>
                  <Field>
                    <Label label='E-mail verificado' size='sm' />
                    <Input value={userData.firebaseData.emailVerified ? 'Sim' : 'Não'} readOnly />
                  </Field>
                  <Field>
                    <Label label='Último acesso' size='sm' />
                    <Input value={formatFirebaseDate(userData.firebaseData.lastSignInTime)} readOnly />
                  </Field>
                </>
              ) : null}
            </>
          ) : null}
        </Container>
      </Wrapper>
    </Handle>
  )
}

export default UserFetchScreen
